import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { dataAPI } from '../../helpers/api_service';
import loadingService from '../../helpers/loading_service';
import emptyDocIcon from '../../assets/empty_doc.svg';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import UploadDocumentTabs from './UploadDocumentTabs';
import DocumentForm from './UploadDocumentForm';
import DocumentList from './DocumentList';
import DocumentFolders from './DocumentFolders';
import CreateFolderModal from './CreateFolderModal';
import './Documents.css';

const Documents = () => {
  const { mode } = useThemeMode();
  const { hasAddPermission } = usePermissions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [selectedFolderForRename, setSelectedFolderForRename] = useState(null);
  const [editData, setEditData] = useState(null);
  const [folders, setFolders] = useState([]);
  const [remainingMessage, setRemainingMessage] = useState('');
  const [documents, setDocuments] = useState([]);
  const [currentView, setCurrentView] = useState('folders'); // 'folders' or 'documents'
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [documentStats, setDocumentStats] = useState({
    expired_count: 0,
    expiring_soon_count: 0,
    total_count: 0
  });
  const [toastMessage, setToastMessage] = useState('');

  // Fetch folders when component mounts
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const fetchFolders = async () => {
      try {
        setIsLoading(true);
        setError('');
        
        // Start global loading progress bar
        loadingService.showLoading('Loading folders...');
        
        const response = await dataAPI.getFolderList({ showLoading: false });
        console.log('Folders fetched successfully:', response);
        setFolders(response.data?.folders || []);
        setRemainingMessage(response.data?.remaining_message || '');
      } catch (error) {
        console.error('Error fetching folders:', error);
        setError(error.message || 'Failed to fetch folders');
        setFolders([]);
        setRemainingMessage('');
      } finally {
        setIsLoading(false);
        loadingService.hideLoading();
      }
    };

    fetchFolders();
  }, []);

  // Fetch documents for a specific folder
  const fetchDocumentsForFolder = async (folderId) => {
    try {
      setIsLoading(true);
      setError('');
      const response = await dataAPI.getDocumentsByFolder(folderId);
      console.log('Documents fetched successfully:', response);
      setDocuments(response.data?.documents || response?.documents || []);
      setDocumentStats({
        expired_count: response.data?.expired_count || 0,
        expiring_soon_count: response.data?.expiring_soon_count || 0,
        total_count: response.data?.total_count || 0
      });
      
      // Reset expired view if no expired documents exist
      if ((response.data?.expired_count || 0) === 0) {
        setShowExpiredOnly(false);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError(error.message || 'Failed to fetch documents');
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Immutable merge of documents preserving unchanged references
  const fieldsUsedInUI = useMemo(() => ['name','expiry_date','version','file_path','updated_at','status','processing_status','id'], []);
  const shallowChanged = useCallback((a, b) => fieldsUsedInUI.some(k => (a?.[k] ?? null) !== (b?.[k] ?? null)), [fieldsUsedInUI]);
  const mergeDocuments = useCallback((prevDocs, nextDocs) => {
    const prevById = new Map(prevDocs.map(d => [d.id, d]));
    const nextById = new Map(nextDocs.map(d => [d.id, d]));
    // Keep ordering from nextDocs if provided, else preserve prev order
    const base = nextDocs.length ? nextDocs : prevDocs;
    return base.map(item => {
      const prev = prevById.get(item.id);
      const next = nextById.get(item.id) || item;
      if (!prev) return next; // new document
      return shallowChanged(prev, next) ? { ...prev, ...next } : prev;
    });
  }, [shallowChanged]);

  // Listen for app notifications and selectively update document rows without page refresh
  useEffect(() => {
    if (currentView !== 'documents') return;
    const handler = async (e) => {
      try {
        const detail = e?.detail || {};
        const docId = detail?.data?.document_id || detail?.data?.doc_id || null;
        // If user is not in a folder yet, ignore
        if (!selectedFolder) return;

        if (docId) {
          // Fetch all docs for the folder and merge the changed one(s) without global loader
          const res = await dataAPI.getDocumentsByFolder(selectedFolder.id, { showLoading: false });
          const latest = res.data?.documents || res.documents || [];
          setDocuments(prev => mergeDocuments(prev, latest));
        } else {
          // Fallback: refresh current folder list without global loader
          const res = await dataAPI.getDocumentsByFolder(selectedFolder.id, { showLoading: false });
          const latest = res.data?.documents || res.documents || [];
          setDocuments(prev => mergeDocuments(prev, latest));
        }
      } catch (err) {
        console.error('Selective document refresh failed:', err);
      }
    };
    window.addEventListener('complimate:notification', handler);
    return () => window.removeEventListener('complimate:notification', handler);
  }, [currentView, mergeDocuments, selectedFolder]);

  const handleDeleteDocument = (id) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  };

  const showToast = useCallback((message) => {
    setToastMessage(message);
  }, []);

  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const handleDownloadDocument = useCallback(async (doc) => {
    if (!doc?.id) return;
    try {
      const response = await dataAPI.downloadDocument(doc.id, { showLoading: false });
      if (response?.status === 'success' && response?.data?.download_url) {
        const downloadUrl = response.data.download_url;
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      } else {
        const message = response?.message || 'Failed to generate download link.';
        showToast(message);
      }
    } catch (error) {
      const message = error?.message || 'Failed to download document.';
      showToast(message);
    }
  }, [showToast]);

  const handleEditDocument = (document) => {
    setEditData(document);
    setIsFormOpen(true);
  };

  const handleFolderClick = (folder) => {
    setSelectedFolder(folder);
    setCurrentView('documents');
    fetchDocumentsForFolder(folder.id);
  };

  const handleBackToFolders = () => {
    setCurrentView('folders');
    setSelectedFolder(null);
    setDocuments([]);
    setError('');
    setShowExpiredOnly(false); // Reset expired view when going back to folders
  };

  // Filter folders based on search term
  const filteredFolders = folders;
  

  // Filter documents based on expired status
  const filteredDocuments = showExpiredOnly 
    ? documents.filter(doc => doc.status === 'Expired')
    : documents;

  // Toggle between showing all documents and expired only
  const handleToggleExpiredView = () => {
    setShowExpiredOnly(!showExpiredOnly);
  };

  return (
    <div
      className="documents-page"
      style={{
        padding: '20px',
        width: '100%',
        height: '100%',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff'
      }}
    >
      {/* Loading State - Now handled by global LoadingProgressBar */}

      {/* Error State */}
      {!isLoading && error && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '16px',
            color: '#f44336',
            marginBottom: '16px',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {error}
          </div>
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: '#26C3C0',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Content State */}
      {!isLoading && !error && currentView === 'folders' && filteredFolders.length === 0 ? (
        // Empty Folders State
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
          textAlign: 'center'
        }}>
          {/* Empty Document Icon */}
          <div style={{
            marginBottom: '24px'
          }}>
            <img 
              src={emptyDocIcon} 
              alt="No Folders" 
              style={{
                width: '71px',
                height: '59px',
                filter: mode === 'dark' ? 'brightness(1)' : 'brightness(0.8)'
              }}
            />
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            margin: '0 0 16px 0',
            fontFamily: 'Urbanist, Arial, sans-serif',
            lineHeight: '1.2'
          }}>
            No Folders Yet
          </h1>

          {/* Description */}
          <p style={{
            fontSize: '16px',
            color: mode === 'dark' ? '#FFFFFF' : '#666666',
            margin: '0 0 32px 0',
            maxWidth: '600px',
            lineHeight: '1.5',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            Organize your documents into folders for better management. Create folders to group related documents and maintain a clean structure.
          </p>

          {/* Create Folder Button */}
          {hasAddPermission('document') && (
            <button 
              onClick={() => setIsFolderModalOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#26C3C0',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#22B0AD';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#26C3C0';
              }}
            >
              <AddIcon style={{ fontSize: '20px' }} />
              Create Folder
            </button>
          )}
        </div>
      ) : !isLoading && !error && currentView === 'documents' && filteredDocuments.length === 0 ? (
        // Empty Documents State
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
          textAlign: 'center'
        }}>
          {/* Empty Document Icon */}
          <div style={{
            marginBottom: '24px'
          }}>
            <img 
              src={emptyDocIcon} 
              alt="No Documents" 
              style={{
                width: '71px',
                height: '59px',
                filter: mode === 'dark' ? 'brightness(1)' : 'brightness(0.8)'
              }}
            />
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            margin: '0 0 16px 0',
            fontFamily: 'Urbanist, Arial, sans-serif',
            lineHeight: '1.2'
          }}>
            {showExpiredOnly ? 'No Expired Documents' : 'No Documents in This Folder'}
          </h1>

          {/* Description */}
          <p style={{
            fontSize: '16px',
            color: mode === 'dark' ? '#FFFFFF' : '#666666',
            margin: '0 0 32px 0',
            maxWidth: '600px',
            lineHeight: '1.5',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {showExpiredOnly 
              ? 'All your documents are up to date and compliant.'
              : `The folder "${selectedFolder?.name}" is empty. Upload documents to get started.`
            }
          </p>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {/* Back to Folders Button */}
            <button 
              onClick={handleBackToFolders}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                color: mode === 'dark' ? '#FFFFFF' : '#666666',
                border: `1px solid ${mode === 'dark' ? '#333' : '#ddd'}`,
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              ← Back to Folders
            </button>

            {/* Upload Button */}
            {hasAddPermission('document') && (
              <button 
                onClick={() => setIsFormOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#26C3C0',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  transition: 'background-color 0.2s ease',
                  boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#22B0AD';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#26C3C0';
                }}
              >
                <AddIcon style={{ fontSize: '20px' }} />
                Upload Document
              </button>
            )}
          </div>
        </div>
      ) : (!isLoading && !error && currentView === 'folders' && filteredFolders.length > 0) ? (
        // Folders View
        <div>
          {/* Header */}
          <div
            className="documents-header"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}
          >
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              margin: '0',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              Folders
            </h1>
            {hasAddPermission('document') && (
              <div className="documents-header-actions" style={{ display: 'flex' }}>
              <button
                onClick={() => setIsFolderModalOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#26C3C0',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  transition: 'background-color 0.2s ease',
                  boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#22B0AD';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#26C3C0';
                }}
              >
                <AddIcon style={{ fontSize: '20px' }} />
                Create Folder
              </button>
              </div>
            )}
          </div>

          {/* Folders Table */}
          <DocumentFolders
            folders={folders}
            remainingMessage={remainingMessage}
            onFolderClick={handleFolderClick}
            onFolderUpdate={() => {
              // Refresh folders after any operation
              const fetchFolders = async () => {
                try {
                  const response = await dataAPI.getFolderList({ showLoading: false });
                  setFolders(response.data?.folders || []);
                  setRemainingMessage(response.data?.remaining_message || '');
                } catch (error) {
                  console.error('Error refreshing folders:', error);
                }
              };
              fetchFolders();
            }}
            onRenameFolder={(folder) => {
              setSelectedFolderForRename(folder);
              setIsRenameModalOpen(true);
            }}
          />
        </div>
      ) : (!isLoading && !error && currentView === 'documents' && filteredDocuments.length > 0) ? (
        // Documents View
        <div>
          {/* Breadcrumb Navigation and Upload Button */}
          <div
            className="documents-breadcrumb-row"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              padding: '12px 16px'
            }}
          >
            {/* Left side - Breadcrumb */}
            <div
              className="documents-breadcrumb-left"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <button
                onClick={handleBackToFolders}
                style={{
                  background: 'none',
                  border: 'none',
                  color: mode === 'dark' ? '#26C3C0' : '#007bff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = mode === 'dark' ? 'rgba(38, 195, 192, 0.1)' : 'rgba(0, 123, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                ← Folders
              </button>
              <span style={{
                color: mode === 'dark' ? '#666' : '#999',
                fontSize: '14px'
              }}>
                /
              </span>
              <span style={{
                color: mode === 'dark' ? '#FFFFFF' : '#333333',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                {selectedFolder?.name === 'Multiple Upload' ? 'Documents' : selectedFolder?.name}
              </span>
            </div>

            {/* Right side - Upload Button */}
            {hasAddPermission('document') && (
              <button
                className="documents-upload-btn"
                onClick={() => setIsFormOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#26C3C0',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  transition: 'background-color 0.2s ease',
                  boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#22B0AD';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#26C3C0';
                }}
              >
                <AddIcon style={{ fontSize: '20px' }} />
                Upload Document
              </button>
            )}
          </div>

          {/* Expired Documents Warning */}
          {documentStats.expired_count > 0 && (
            <div
              className="documents-expired-warning"
              style={{
                backgroundColor: mode === 'dark' ? '#1a0000' : '#fff5f5',
                border: '1px solid #ff4444',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <WarningIcon style={{
                  color: '#ff4444',
                  fontSize: '20px'
                }} />
                <span style={{
                  color: mode === 'dark' ? '#ffffff' : '#d32f2f',
                  fontSize: '14px',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  lineHeight: '1.4'
                }}>
                  {showExpiredOnly 
                    ? `Showing ${documentStats.expired_count} expired document${documentStats.expired_count > 1 ? 's' : ''}.`
                    : `You currently have ${documentStats.expired_count} expired document${documentStats.expired_count > 1 ? 's' : ''} in your account. Please take a moment to review and renew ${documentStats.expired_count > 1 ? 'them' : 'it'} to maintain compliance.`
                  }
                </span>
              </div>
              <div className="documents-expired-warning-actions" style={{ display: 'flex' }}>
              <button
                onClick={handleToggleExpiredView}
                style={{
                  background: 'transparent',
                  color: '#26C3C0',
                  border: 'none',
                  padding: '6px 12px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(38, 195, 192, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                {showExpiredOnly ? 'View All' : 'View Expired'}
              </button>
              </div>
            </div>
          )}

          {/* Document List */}
          <DocumentList
            documents={filteredDocuments}
            onDeleteDocument={handleDeleteDocument}
            onEditDocument={handleEditDocument}
            onDownloadDocument={handleDownloadDocument}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showSearchOnly={false}
          />
        </div>
      ) : null}

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        onSuccess={() => {
          // Refresh folders after creation
          const fetchFolders = async () => {
            try {
              const response = await dataAPI.getFolderList();
              setFolders(response.data?.folders || []);
              setRemainingMessage(response.data?.remaining_message || '');
            } catch (error) {
              console.error('Error refreshing folders:', error);
            }
          };
          fetchFolders();
        }}
      />

      {/* Rename Folder Modal */}
      <CreateFolderModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        onSuccess={() => {
          // Refresh folders after rename
          const fetchFolders = async () => {
            try {
              const response = await dataAPI.getFolderList();
              setFolders(response.data?.folders || []);
              setRemainingMessage(response.data?.remaining_message || '');
            } catch (error) {
              console.error('Error refreshing folders:', error);
            }
          };
          fetchFolders();
        }}
        mode="edit"
        folder={selectedFolderForRename}
      />

      {/* Document Form */}
      {editData ? (
        /* Edit Mode - Show Original DocumentForm */
        <DocumentForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditData(null);
          }}
          editData={editData}
          selectedFolder={selectedFolder}
          onSubmit={(response) => {
            // Handle successful edit response
            console.log('Document edit successful:', response);
            // Refresh the current view
            if (currentView === 'documents' && selectedFolder) {
              fetchDocumentsForFolder(selectedFolder.id);
            } else {
              // Refresh folders
              const fetchFolders = async () => {
                try {
                  const response = await dataAPI.getFolderList({ showLoading: false });
                  setFolders(response.data?.folders || []);
                } catch (error) {
                  console.error('Error refreshing folders:', error);
                }
              };
              fetchFolders();
            }
          }}
        />
      ) : (
        /* New Upload Mode - Show Tabbed Interface */
        <UploadDocumentTabs
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditData(null);
          }}
          editData={editData}
          selectedFolder={selectedFolder}
          onSubmit={(response) => {
            // Handle successful upload response
            console.log('Document upload successful:', response);
            // Refresh the current view
            if (currentView === 'documents' && selectedFolder) {
              fetchDocumentsForFolder(selectedFolder.id);
            } else {
              // Refresh folders
              const fetchFolders = async () => {
                try {
                  const response = await dataAPI.getFolderList({ showLoading: false });
                  setFolders(response.data?.folders || []);
                } catch (error) {
                  console.error('Error refreshing folders:', error);
                }
              };
              fetchFolders();
            }
          }}
        />
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: mode === 'dark' ? '#1a0f0f' : '#fff5f5',
          border: '1px solid #ff4444',
          borderRadius: '8px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)',
          zIndex: 2000,
          minWidth: '280px',
          maxWidth: '360px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <WarningIcon style={{
              color: '#ff4444',
              fontSize: '20px'
            }} />
            <span style={{
              color: mode === 'dark' ? '#ffffff' : '#d32f2f',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              lineHeight: '1.4',
              flex: 1
            }}>
              {toastMessage}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents; 
